/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router';
import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toSafeInteger from 'lodash/toSafeInteger';
import isNil from 'lodash/isNil';

import {
    MediaGallery,
    Heading,
    DisplayHtmlContent,
    ReadMoreBlock,
    Button,
    ScrollToHelper,
    SkipLink,
} from 'sarsaparilla';

import GettingHereShared from 'ui-permit/shared-components/venuePage/GettingHereShared';
import RightRailContainer from 'ui-permit/shared-components/venuePage/RightRail';
import VenueTabWrapper from 'ui-permit/shared-components/venuePage/VenueTabWrapper';
import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';
import {
    RatingSummaryContainer,
    CellSummaryContainer,
} from 'ui-rating-review/src/containers/AggregateBar';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { SharedVenuePage } from 'shared-ui/components/SharedVenuePage/SharedVenuePage';

import PermitMap from 'ui-permit/src/shared/components/PermitMap';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';
import { getCurrentTime, readRuleAsMoment } from '../utils/permitUtilsNew';
import CooperatorsList from '../components/CooperatorsList';
import PermitVenueDates from '../components/PermitVenueDates';
import * as pActions from '../actions/permit';
import { BOUNDARY_WATERS_PERMIT_ID } from '../constants';

const propTypes = {
    permit: PropTypes.shape({
        activities: PropTypes.array,
        addresses: PropTypes.array,
        org_code: PropTypes.string,
        description: PropTypes.shape({
            description: PropTypes.string,
            quota_details: PropTypes.string,
            season_information: PropTypes.string,
            lottery_information: PropTypes.string,
            need_to_know_permit: PropTypes.string,
            fee_policy: PropTypes.string,
            cancellation_policy: PropTypes.string,
            no_show_policy: PropTypes.string,
            discounts: PropTypes.string,
            driving_directions: PropTypes.string,
        }),
        time_zone: PropTypes.any,
        directions: PropTypes.string,
        divisions: PropTypes.shape({}),
        id: PropTypes.string,
        indexed_rules: PropTypes.shape({
            SeasonStart: PropTypes.shape({
                value: PropTypes.number,
            }),
            SeasonEnd: PropTypes.shape({
                value: PropTypes.number,
            }),
            HighUseRegistrationStart: PropTypes.shape({
                value: PropTypes.number,
            }),
            HighUseRegistrationEnd: PropTypes.shape({
                value: PropTypes.number,
            }),
            LotteryResultsAvailable: PropTypes.shape({
                value: PropTypes.number,
            }),
            CancellationDeadline: PropTypes.shape({
                value: PropTypes.number,
            }),
            HighUseSeasonStart: PropTypes.shape({
                value: PropTypes.number,
            }),
            HighUseSeasonEnd: PropTypes.shape({
                value: PropTypes.number,
            }),
            QuotaPerDay: PropTypes.shape({
                value: PropTypes.number,
            }),
            DisplayDate: PropTypes.shape({
                value: PropTypes.number,
            }),
        }),
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        links: PropTypes.array,
        lotteries: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                is_active: PropTypes.bool,
            })
        ),
        lottery_fee: PropTypes.string,
        name: PropTypes.string,
        needToKnow: PropTypes.arrayOf(PropTypes.string),
        phone: PropTypes.string,
        recareas: PropTypes.array,
        recreation_fee: PropTypes.string,
        reservation_fee: PropTypes.string,
    }),
    dispatch: PropTypes.func,
    error: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                error: PropTypes.string,
            }),
        ])
    ),
    recareaError: PropTypes.string,
    aggSeasonRules: PropTypes.array,
    ratingReviews: PropTypes.object,
    currentDate: PropTypes.any,
    activeLottery: PropTypes.any,
};

const defaultProps = {
    permit: {
        links: [],
        name: '',
    },
};

const breadcrumbs = [
    { title: 'Home', url: '/' },
    { title: 'Superior National Forest', url: getGatewayUrl('1085') },
    { title: 'Boundary Waters Canoe Area Wilderness' },
];

const partOf = { title: 'Superior National Forest', url: getGatewayUrl('1085') };

function PermitVenuePage({
    error = [],
    recareaError = [],
    permit,
    ratingReviews,
    aggSeasonRules,
    dispatch,
    currentDate,
    activeLottery,
}) {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const tabNumber = toSafeInteger(searchParams.get('activeTab'));
    let activeTabIndex;

    if (location.pathname.indexOf('/cooperators') >= 0) {
        activeTabIndex = 5;
    } else {
        activeTabIndex = tabNumber < 0 || tabNumber >= 6 ? 0 : tabNumber;
    }

    const permitId = BOUNDARY_WATERS_PERMIT_ID;

    const { latitude, longitude } = permit;
    const [activeTabIndexState, setActiveTabIndexState] = useState(activeTabIndex);
    const [linesState, setLinesState] = useState(null);
    const [pointsState, setPointsState] = useState(null);
    const [polygonsState, setPolygonsState] = useState(null);
    const [shouldScrollToImportantDatesState, setShouldScrollToImportantDatesState] =
        useState(false);

    useEffect(() => {
        axios
            .get(
                `/shared/boundarywaters/data/${BOUNDARY_WATERS_PERMIT_ID}/features.json`,
                {
                    credentials: 'include',
                }
            )
            .then((json) => {
                setLinesState(json.data.lines);
                setPointsState(json.data.points);
                setPolygonsState(json.data.polygons);
            })
            .catch(() => {});

        dispatch(pActions.fetchPermit(BOUNDARY_WATERS_PERMIT_ID));
        dispatch(pActions.fetchAggregateSeason(BOUNDARY_WATERS_PERMIT_ID));
    }, []);

    // if user clicks on Important Dates button we should first switch tab and then scroll element into view
    useEffect(() => {
        if (shouldScrollToImportantDatesState && activeTabIndexState === 0) {
            const el = document.getElementById('important-dates');
            const header = document.querySelector('header');
            const currentPosition = window.scrollY;
            const y =
                el.getBoundingClientRect().top - header.offsetHeight + currentPosition;
            ScrollToHelper.scrollToY(y);
            el.focus();
            setShouldScrollToImportantDatesState(false);
        }
    }, [shouldScrollToImportantDatesState, activeTabIndexState]);

    // Fire the view_item GA Event
    useSendGAEvent({
        itemLevel: 'Facility',
        facilityId: permitId,
        itemCategory: INVENTORY_PERMIT,
        isDataLoading: !permit?.id,
    });

    const handleImportantDates = useCallback(() => {
        setShouldScrollToImportantDatesState(true);
        setActiveTabIndexState(0);
    }, [setShouldScrollToImportantDatesState, setActiveTabIndexState]);

    const isInSeason = () => {
        if (!permit?.id) return false;
        // Use management years/display date to determine if the permit dates are able to be viewed
        const currentDateUpdated = getCurrentTime(currentDate);
        const displayStart = readRuleAsMoment(
            'DisplayDate',
            aggSeasonRules,
            permit.time_zone,
            undefined,
            true
        );
        const displayEnd = readRuleAsMoment(
            'SeasonEnd',
            aggSeasonRules,
            permit.time_zone,
            undefined,
            true
        );
        return (
            displayStart.isValid() &&
            displayEnd.isValid() &&
            currentDateUpdated.isBetween(displayStart, displayEnd, 'minute', '[]')
        );
    };

    const isRegistrationClosed = () => !activeLottery && !isInSeason();

    const onSelectedTabIndexChanged = (index) => {
        setActiveTabIndexState(index);

        if (index === 5) {
            window.history.replaceState({}, '', '/permits/233396/cooperators');
        } else {
            window.history.replaceState({}, '', '/permits/233396');
        }
    };

    const renderOverview = () => {
        const {
            description,
            season_information: seasonInformation,
            quota_details: quotaDetails,
        } = permit.description;

        return (
            <VenueTabWrapper permitId={permit.id}>
                <div className="content-section">
                    <ReadMoreBlock>
                        <DisplayHtmlContent html={description} />
                    </ReadMoreBlock>
                </div>

                {seasonInformation && (
                    <div className="content-section">
                        <Heading appearance="h5" headingLevel={2}>
                            Permit & Season Information
                        </Heading>
                        <DisplayHtmlContent html={seasonInformation} />
                    </div>
                )}

                <div className="content-section">
                    <PermitVenueDates permit={permit} />
                </div>

                {quotaDetails && (
                    <div className="content-section">
                        <Heading appearance="h5" headingLevel={2}>
                            How Does the Quota Work for this Permit?
                        </Heading>
                        <DisplayHtmlContent html={quotaDetails} />
                    </div>
                )}
                <SkipLink href="#sidebar-anchor">Skip To Right Sidebar</SkipLink>
                <MediaGallery
                    assetId={BOUNDARY_WATERS_PERMIT_ID}
                    className="content-section"
                />
            </VenueTabWrapper>
        );
    };

    const renderContent = (body, title) => {
        if (body) {
            return (
                <>
                    {title && (
                        <Heading appearance="h5" headingLevel={2}>
                            {title}
                        </Heading>
                    )}
                    <DisplayHtmlContent html={body} />
                </>
            );
        }
        return null;
    };

    const renderNeedToKnow = () => {
        const { need_to_know_permit: needToKnowPermit } = permit.description;

        return (
            <VenueTabWrapper permitId={permitId}>
                {renderContent(needToKnowPermit)}
            </VenueTabWrapper>
        );
    };

    const renderPolicy = () => {
        const {
            fee_policy: feePolicy,
            cancellation_policy: cancellationPolicy,
            no_show_policy: noShowPolicy,
            discounts,
        } = permit.description;

        return (
            <VenueTabWrapper permitId={permitId}>
                <div className="content-section">
                    {renderContent(feePolicy, 'Fee Policies')}
                </div>
                <div className="content-section">
                    {renderContent(cancellationPolicy, 'Cancellation & Change Policies')}
                </div>
                {noShowPolicy && (
                    <div className="content-section">
                        {renderContent(noShowPolicy, 'No Show Policies')}
                    </div>
                )}
                {discounts && (
                    <div className="content-section">
                        {renderContent(discounts, 'Discount Pass Policies')}
                    </div>
                )}
            </VenueTabWrapper>
        );
    };

    const renderContacts = () => {
        const description1 = get(permit, 'addresses[0].description1');
        const description2 = get(permit, 'addresses[0].description2');
        const remarks = get(permit, 'addresses[0].remarks');
        return (
            <VenueTabWrapper permitId={permitId}>
                {permit.addresses && permit.addresses.length > 0 && (
                    <div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading appearance="h5" headingLevel={2}>
                                    Mailing Address
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col">
                                <div translate="no">
                                    {description1 && !isEmpty(description1) ? (
                                        <div>{description1}</div>
                                    ) : null}
                                    {description2 && !isEmpty(description2) ? (
                                        <div>{description2}</div>
                                    ) : null}
                                    <div>{permit.addresses[0].street_address1}</div>
                                    <div>{permit.addresses[0].street_address2}</div>
                                    <div>
                                        {permit.addresses[0].city},{' '}
                                        {permit.addresses[0].state_code}{' '}
                                        {permit.addresses[0].postal_code}
                                    </div>
                                </div>
                                {remarks && !isEmpty(remarks) ? (
                                    <div>{remarks}</div>
                                ) : null}
                            </div>
                        </div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading appearance="h5" headingLevel={2}>
                                    Phone Number
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col">
                                <p>{permit.phone}</p>
                            </div>
                        </div>
                    </div>
                )}
            </VenueTabWrapper>
        );
    };

    const renderCooperators = () => {
        return (
            <VenueTabWrapper permitId={permitId}>
                <p>
                    A commercial permit issue station is a business that has an agreement
                    with the Forest Service to issue mandatory quota permits at their
                    location.
                    <ul>
                        <li>
                            Review hours of operation and locations carefully and select
                            an issue station near your entry point.
                        </li>
                        <li>
                            Only the permit holder or alternate permit holders specified
                            at the time of reservation can pick up the permit (photo I.D.
                            required).
                        </li>
                        <li>
                            Permits must be picked up on the entry date or one day prior
                            at the location specified.
                        </li>
                        <li>
                            Issue stations may be changed online at{' '}
                            <a href={`${process.env.SITE_ROOT}`}>
                                {process.env.SITE_NAME}
                            </a>{' '}
                            or by calling the contact center or Forest Service.
                        </li>
                        <li>
                            Commercial issue stations may charge a service fee (up to $2)
                            for issuing overnight permits.
                        </li>
                    </ul>
                </p>
                <CooperatorsList />
            </VenueTabWrapper>
        );
    };

    const renderSide = () => {
        return (
            <p>
                Permits are required year around, but various factors determine which
                permits you need and how to acquire them.
            </p>
        );
    };

    const renderButtonList = () => {
        return [
            <Button
                appearance="primary"
                size="md"
                className="my-2"
                shouldFitContainer
                to="/permits/233396/registration/detailed-availability"
                key="explore"
            >
                Explore available permits
            </Button>,
            <Button
                aria-label="Walk Me Through Questionnaire"
                appearance="tertiary"
                size="md"
                className="my-2"
                shouldFitContainer
                to="/permits/233396/questionnaire"
                key="walk"
            >
                Walk me through
            </Button>,
        ];
    };

    const renderRightRail = () => {
        const isClosed = isRegistrationClosed();
        const availablePermits = renderSide();
        const buttonList = !isClosed ? renderButtonList() : [];

        return RightRailContainer({
            permit,
            isClosed,
            isLoading: !permit?.id,
            handleImportantDates,
            children: [availablePermits],
            buttonList,
        });
    };

    const tabsToShow = () => {
        if (!permit?.id) return [];
        const { driving_directions: drivingDirections } = permit.description;

        return [
            {
                label: 'Overview',
                content: renderOverview(),
            },
            {
                label: 'Need to Know',
                content: renderNeedToKnow(),
            },
            {
                label: 'Fees & Cancellations',
                content: renderPolicy(),
            },
            {
                label: 'Getting Here',
                content: (
                    <GettingHereShared
                        permitId={permitId}
                        longitude={longitude}
                        latitude={latitude}
                        drivingDirections={drivingDirections}
                    />
                ),
            },
            {
                label: 'Contacts',
                content: renderContacts(),
            },
            {
                label: 'Issue Stations',
                content: renderCooperators(),
            },
        ];
    };

    const map = permit?.id &&
        location?.pathname?.toLowerCase().indexOf('cooperators') === -1 && (
            <PermitMap
                geoJsonCenterPoint={{
                    geometry: {
                        coordinates: [permit?.longitude, permit?.latitude],
                        type: 'Point',
                    },
                    properties: {
                        name: permit?.name,
                        type: 'Permit',
                    },
                    type: 'Feature',
                }}
                geoJsonLines={linesState}
                geoJsonPoints={pointsState}
                geoJsonPolygons={polygonsState}
                mapContainer="map"
            />
        );

    const ratingsPresent = get(ratingReviews, 'number_of_ratings', 0) !== 0;
    const cellCoveragePresent = !isNil(
        get(ratingReviews, 'aggregate_cell_coverage_ratings')
    );
    return (
        <div className="boundarywaters">
            <SharedVenuePage
                map={map}
                assetId={permitId}
                favoriteEntityId={permitId}
                favoriteEntityType={'permit'}
                errors={[...error, ...recareaError]}
                tabs={tabsToShow()}
                activeTabIndex={activeTabIndexState}
                onActiveTabIndexChange={onSelectedTabIndexChanged}
                mapLatitude={permit?.latitude}
                mapLongitude={permit?.longitude}
                starRating={
                    ratingsPresent && (
                        <RatingSummaryContainer
                            locationType={'Permit'}
                            locationId={permitId}
                        />
                    )
                }
                cellRating={
                    cellCoveragePresent && (
                        <CellSummaryContainer
                            locationType={'Permit'}
                            locationId={permitId}
                        />
                    )
                }
                isLoading={!permit?.id} // {isLoadingPermitContent}
                title={permit?.name || ''}
                inventoryType="Permit"
                breadcrumbPath={breadcrumbs}
                partOf={partOf}
                rightRail={renderRightRail()}
            />
        </div>
    );
}

PermitVenuePage.propTypes = propTypes;
PermitVenuePage.defaultProps = defaultProps;

function select(state) {
    return {
        activeLottery: state.boundarywaters.permit.lottery,
        error: state.boundarywaters.permit.error,
        navigation: state.navigation,
        permit: state.boundarywaters.permit.result,
        recareaError: state.boundarywaters.permit.recarea_err,
        aggSeasonRules: state.boundarywaters.agg_season_rules,
        ratingReviews: state.ratingReviews.fetchAggregate.data,
    };
}

export default connect(select)(PermitVenuePage);

// cSpell:ignore recarea, recareas, boundarywaters
